import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import systemModuleService, { SystemModule } from '../../services/system-module.service';
import { NumberTextField } from '../../components/common';
import authService from '../../services/auth.service';

const ModuleManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [modules, setModules] = useState<SystemModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingModule, setEditingModule] = useState<SystemModule | null>(null);
  const [formData, setFormData] = useState({
    moduleId: '',
    name: '',
    description: '',
    icon: '',
    order: 0,
  });

  useEffect(() => {
    checkAdminAccess();
    fetchModules();
  }, []);

  const checkAdminAccess = () => {
    const user = authService.getCurrentUser();
    const userRole = user?.role?.type?.toLowerCase();
    // Check if user is admin or super admin
    const adminRoles = ['admin', 'super_admin', 'administrator'];
    const hasAdminAccess = adminRoles.includes(userRole);
    setIsAdmin(hasAdminAccess);
    
    if (!hasAdminAccess) {
      setError('Access denied. Only administrators can manage modules.');
    }
  };

  const fetchModules = async () => {
    try {
      setLoading(true);
      const response = await systemModuleService.getAllModules();
      setModules(response.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch modules');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModule = async (module: SystemModule) => {
    try {
      await systemModuleService.updateModuleStatus(module.id, !module.isActive);
      await fetchModules();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update module status');
    }
  };

  const handleOpenDialog = (module?: SystemModule) => {
    if (module) {
      setEditingModule(module);
      setFormData({
        moduleId: module.moduleId,
        name: module.name,
        description: module.description || '',
        icon: module.icon || '',
        order: module.order,
      });
    } else {
      setEditingModule(null);
      setFormData({
        moduleId: '',
        name: '',
        description: '',
        icon: '',
        order: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingModule(null);
  };

  const handleSaveModule = async () => {
    try {
      if (editingModule) {
        await systemModuleService.updateModule(editingModule.id, formData);
      } else {
        await systemModuleService.createModule(formData);
      }
      handleCloseDialog();
      await fetchModules();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save module');
    }
  };

  const handleDeleteModule = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this module?')) {
      try {
        await systemModuleService.deleteModule(id);
        await fetchModules();
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to delete module');
      }
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography>Loading modules...</Typography>
      </Box>
    );
  }

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <SecurityIcon fontSize="large" color="error" />
          <Typography variant="h4">Module Management</Typography>
        </Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body2">
            Only administrators can manage system modules. Please contact your system administrator if you need access to this feature.
          </Typography>
        </Alert>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecurityIcon fontSize="large" color="primary" />
          <Typography variant="h4">Module Management</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Module
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Modules control which sections of the system are available. Active modules can be assigned to roles.
          Dashboard and Settings modules are always visible to all users.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        gap: 3 
      }}>
        {modules.map((module) => (
          <Card key={module.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Typography variant="h6" component="div">
                    {module.name}
                  </Typography>
                  <Chip
                    label={module.isActive ? 'Active' : 'Inactive'}
                    color={module.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  ID: {module.moduleId}
                </Typography>
                {module.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {module.description}
                  </Typography>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      checked={module.isActive}
                      onChange={() => handleToggleModule(module)}
                      color="primary"
                    />
                  }
                  label={module.isActive ? 'Enabled' : 'Disabled'}
                />
              </CardContent>
              <CardActions>
                <Tooltip title="Edit Module">
                  <IconButton size="small" onClick={() => handleOpenDialog(module)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Module">
                  <IconButton size="small" onClick={() => handleDeleteModule(module.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="User Access">
                  <IconButton size="small" href={`/modules/user-access/${module.id}`}>
                    <PeopleIcon />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
        ))}
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingModule ? 'Edit Module' : 'Add Module'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Module ID"
              value={formData.moduleId}
              onChange={(e) => setFormData({ ...formData, moduleId: e.target.value })}
              required
              disabled={!!editingModule}
              helperText="Unique identifier (e.g., 'hr', 'inventory', 'crm')"
            />
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
            />
            <TextField
              label="Icon"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              helperText="Material UI icon name (optional)"
            />
            <NumberTextField
              label="Order"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              helperText="Display order (lower numbers appear first)"
              allowDecimal={false}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveModule} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModuleManagement;

